import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { sealData, unsealData } from "iron-session";
import { useRouter } from "next/navigation";

import { useSmartSearch } from "@/contexts/useSmartSearch";
import SmartSearchResult from "../SmartSearchResult";
import { SmartSearchMasterData } from "@/libs/types/smart-search.type";
import { ViewFilter } from "@/libs/types/want-to-buy.type";
import { usePageLoadingStore } from "@/contexts/usePageLoading";
import { useWantToBuyStore } from "@/contexts/useWantToBuy";
import { MasterData } from "@/libs/types/master-data.type";
import { SearchHistoyListCookieKey } from "@/libs/constant/smart-search.constant";
import { PagePath } from "@/libs/constant/path";
import { sessionOptions } from "@/libs/configs/dsure.session";
import {getCookieFromServer, setCookieFromServer} from "@/app/api/cookies/cookie";

type SmartSearchInputProps = TextFieldProps & {
  resultContainerSx?: SxProps;
};

type UnsealCookieValue = SmartSearchMasterData[];

const SmartSearchInput = forwardRef<HTMLInputElement, SmartSearchInputProps>(
  ({ onChange, sx, resultContainerSx, ...rest }, ref) => {
    const router = useRouter();
    const BoxContainerRef = useRef<HTMLDivElement | null>(null);
    const ResultContainerRef = useRef<HTMLDivElement | null>(null);

    const [searchHistoryList, setSearchHistoryList] = useState<Nullable<UnsealCookieValue>>(null);

    const { isFocus, searchResult, setSearchText, setIsFocus } = useSmartSearch();
    const { setLoading, resetLoading } = usePageLoadingStore();
    const { generateUrlSlug, setViewFilter } = useWantToBuyStore();

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (BoxContainerRef.current && !BoxContainerRef.current.contains(event.target as Node)) {
          setIsFocus(false);
        }
      },
      [setIsFocus],
    );

    useEffect(() => {
      if (isFocus) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isFocus, handleClickOutside]);

    const onSelectResult = useCallback(
      async (item: SmartSearchMasterData) => {
        try {
          setLoading();
          setViewFilter({
            Brand: item.brand ? ({ Name: item.brand.name } as MasterData) : undefined,
            Model: item.model ? ({ Name: item.model.name } as MasterData) : undefined,
          } as ViewFilter);

          const urlSlug = generateUrlSlug();

          const currentSearchHistory = await getSearchHistoyCookie();
          const isExistHistory = currentSearchHistory?.find(
            (history) => history.fullTextSearch === item.fullTextSearch,
          );
          if (!isExistHistory) {
            const updatedCookie = currentSearchHistory ? [item, ...currentSearchHistory] : [item];
            await setSearchHistoyCookie(updatedCookie);
          }

          router.push(`${PagePath.wantToBuy}/${urlSlug}`);

          setIsFocus(false);
        } catch (error) {
          resetLoading();
          console.error("Error in onSelectResult:", error);
        }
      },
      [generateUrlSlug, router, setIsFocus, setLoading, setViewFilter, resetLoading],
    );

    const getSearchHistoyCookie = async () => {
      const cookieValue = await getCookieFromServer(SearchHistoyListCookieKey);
      return cookieValue
        ? unsealData<UnsealCookieValue>(cookieValue, { password: sessionOptions.password })
        : null;
    };

    const setSearchHistoyCookie = async (updatedCookie: UnsealCookieValue) => {
      const sealed = await sealData(updatedCookie, { password: sessionOptions.password });
      setCookieFromServer(SearchHistoyListCookieKey, sealed, { httpOnly: true, secure: true });
    };

    useEffect(() => {
      const fetchSearchHistory = async () => {
        const result = await getSearchHistoyCookie();
        setSearchHistoryList(result);
      };

      fetchSearchHistory();
    }, []);

    return (
      <Box
        className="test"
        ref={BoxContainerRef}
        sx={{
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        <TextField
          {...rest}
          sx={{
            ...sx,
            ...(isFocus
              ? {
                  borderRadius: {
                    xs: "24px 24px 0px 0px",
                    lg: "24px 24px 0px 0px",
                  },
                }
              : {}),
            transition: "all 0.15s ease-in-out",
          }}
          autoComplete="off"
          inputRef={ref}
          onChange={(e) => {
            onChange && onChange(e);
            setSearchText(e.target.value);
          }}
          onClick={() => setIsFocus(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchResult && searchResult?.length > 0) {
              onSelectResult(searchResult[0]);
            }
          }}
        />
        <SmartSearchResult
          ref={ResultContainerRef}
          containerSx={resultContainerSx}
          searchHistoryList={searchHistoryList}
          onSelect={onSelectResult}
          onClerHistory={() => setSearchHistoryList(null)}
        />
      </Box>
    );
  },
);

SmartSearchInput.displayName = "SmartSearchInput";

export default SmartSearchInput;
